'use client'

import { cn } from '@/lib/tailwind'
import { SparkleIcon } from '@phosphor-icons/react'

interface SectionHeaderProps {
  /** The section title text */
  children: React.ReactNode
  /** Additional className */
  className?: string
}

/**
 * Consistent section header styling for labeled sections.
 * Uses uppercase, muted text with tracking.
 */
export function SectionHeader({ children, className }: SectionHeaderProps) {
  return (
    <p
      className={cn(
        'text-muted-foreground mb-2 text-xs font-medium tracking-wide uppercase',
        className,
      )}
    >
      {children}
    </p>
  )
}

interface EmptyStateProps {
  /** The message to display */
  message: string
  /** Optional additional description */
  description?: string
  /** Optional icon element */
  icon?: React.ReactNode
  /** Additional className */
  className?: string
}

/**
 * Consistent empty state display for lists and tables.
 * Includes a subtle decorative element for visual interest.
 */
export function EmptyState({
  message,
  description,
  icon,
  className,
}: EmptyStateProps) {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center py-16',
        className,
      )}
    >
      {/* Decorative element */}
      <div className="text-muted-foreground/30 mb-4 flex items-center gap-1">
        {icon || (
          <>
            <SparkleIcon className="h-5 w-5" weight="duotone" />
          </>
        )}
      </div>
      <p className="text-muted-foreground text-sm">{message}</p>
      {description && (
        <p className="text-muted-foreground/60 mt-1.5 max-w-xs text-center text-xs">
          {description}
        </p>
      )}
    </div>
  )
}
